import { styled, Typography } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import Box from 'shell/components/Box';
import Tooltip from 'shell/components/Tooltip';
import theme from 'shell/themes';
import { getTooltipProps, stripHTML } from 'utils/helper';

const COLORS = ['default', 'disabled', 'error', 'inherit', 'light', 'primary', 'success'];
const VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sh1', 'sh2', 'p1', 'p2', 'p3', 'p4'];

const CustomTypography = styled(Typography)(({ $color, size, variant, weight, theme }) => {
    const defaultWeight = Object.keys(theme.typography[variant].fontWeight)[0];

    const colorMap = {
        default: theme.palette.ensightful.neutral[900],
        disabled: theme.palette.ensightful.neutral[400],
        error: theme.palette.ensightful.error[700],
        inherit: 'inherit',
        light: theme.palette.ensightful.neutral[700],
        primary: theme.palette.ensightful.primary[500],
        success: theme.palette.ensightful.success[700],
    };

    const color = Object.keys(colorMap).includes($color) ? colorMap[$color] : $color;

    return {
        maxWidth: '100%',
        fontFamily: 'inherit',
        fontSize: `${theme.typography[variant].fontSize[size]}rem`,
        fontWeight: theme.typography[variant].fontWeight[weight || defaultWeight],
        letterSpacing: `${theme.typography[variant].letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight(variant, size)}rem`,
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        color,
        '& p': {
            marginBottom: 0,
            whiteSpace: 'pre-wrap',
        },
    };
});

const RichTextBox = styled(Box)(({ noWrap, theme }) => {
    return {
        whiteSpace: 'pre',
        textWrap: 'wrap',
        '& p': {
            margin: 0,
            minHeight: '1rem',
        },
        '& ul, ol': {
            margin: 0,
        },
        '& ul': {
            listStyleType: '"- "',
        },
        '& a': {
            color: theme.palette.ensightful.primary[500],
            textDecoration: 'underline',
            transition: 'none',
            '&:hover': {
                color: theme.palette.ensightful.primary[700],
                textDecoration: 'underline',
            },
            '&:active': {
                color: theme.palette.ensightful.primary[800],
                textDecoration: 'underline',
            },
        },
        ...(noWrap && {
            display: 'inline-block',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrap: 'unset',
            whiteSpace: 'nowrap',
        }),
    };
});

const trunc = (string, n) => {
    if (string.length <= n) return string;
    const substring = string.slice(0, n - 1);
    return <>{substring.slice(0, substring.lastIndexOf(' '))}&hellip;</>;
};

/**
 * A customizable text component that supports various typography variants, colors, and rich text rendering.
 * It can truncate text, display tooltips, and adapt to different viewports.
 *
 * @param {React.ReactNode} children - The content to display.
 * @param {string|function} [color='default'] - The color of the text. Can be a predefined color string ('default', 'disabled', 'error', 'inherit', 'light', 'primary', 'success') or a function that returns a color.
 * @param {boolean} [rich=false] - Whether the children contain HTML content that should be rendered dangerously.
 * @param {object} [tooltip] - Tooltip configuration object.
 * @param {string|React.ReactElement} tooltip.component - The tooltip content.
 * @param {number} [truncate] - The maximum length of the text before truncation. If provided, the text will be truncated to this length.
 * @param {string} [variant='p2'] - The typography variant. Valid variants are 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sh1', 'sh2', 'p1', 'p2', 'p3', 'p4'.
 * @param {string} [weight] - The font weight. Valid options depend on the variant: for headings (h1-h6) 'medium', 'semibold', 'bold'; for subheadings and paragraphs (sh1, sh2, p1-p4) 'regular', 'medium', 'semibold'. If not provided, the default weight for the specified variant will be used.
 * @param {object} [rest] - Additional props passed to the underlying Typography component.
 * @param {React.Ref} ref - Ref forwarded to the Typography component.
 * @returns {React.ReactElement} The rendered text component.
 */
export default forwardRef(
    ({ children, color = 'default', rich = false, tooltip, truncate, variant = 'p2', ...rest }, ref) => {
        if (tooltip && !tooltip.component) {
            throw new Error(
                `Shell: Invalid tooltip in component Text. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
            );
        }
        if (!COLORS.includes(color) && typeof color !== 'function') {
            throw new Error(`Shell: Invalid color "${color}" in component Text.`);
        }
        if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component Text.`);

        const { device } = useViewport();

        if (typeof color === 'function') color = color(theme);
        const size = device === 'mobile' ? 'small' : 'large';

        if (typeof children === 'string' && truncate) children = trunc(children, truncate);

        if (rich && rest.noWrap) children = stripHTML(children);

        const TextComponent = (
            <CustomTypography ref={ref} $color={color} size={size} variant={variant} {...rest}>
                {rich ? (
                    <RichTextBox
                        component={'span'}
                        dangerouslySetInnerHTML={{ __html: children }}
                        noWrap={rest.noWrap}
                    />
                ) : (
                    children
                )}
            </CustomTypography>
        );

        return tooltip ? <Tooltip {...getTooltipProps(tooltip)}>{TextComponent}</Tooltip> : TextComponent;
    }
);
